import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface ChartSkeletonProps {
	variant?: 'bars' | 'rect' | 'gauge' | 'donut'
	height?: string
	withLegend?: boolean
	legendRows?: number
	className?: string
	label?: string | false
}

const BAR_HEIGHTS = ['h-1/3', 'h-2/3', 'h-1/2', 'h-3/4', 'h-2/5', 'h-5/6', 'h-1/2', 'h-3/5']
const BAR_SLOT_WIDTH = 44
// Below this, legends and the gauge value label eat the plot they annotate.
const MIN_HEIGHT_FOR_LABELS = 120

export function ChartSkeleton({
	variant = 'bars',
	height = 'h-[300px]',
	withLegend = false,
	legendRows = 3,
	className,
	label = 'Loading chart',
}: ChartSkeletonProps) {
	const [rootRef, size] = useElementSize<HTMLDivElement>()
	const barKeys = useStableKeys(fitCount(size.width, BAR_SLOT_WIDTH, 4, 24))
	const legendKeys = useStableKeys(legendRows)
	const isShort = size.height > 0 && size.height < MIN_HEIGHT_FOR_LABELS

	const legend = (
		<div className='flex flex-wrap justify-center gap-4'>
			{legendKeys.map(key => (
				<div key={key} className='flex items-center gap-2'>
					<Skeleton className='size-3 rounded-sm' />
					<Skeleton className='h-3 w-16' />
				</div>
			))}
		</div>
	)

	// `height` sizes the root so the plot fills it via flex-1 (works for fixed heights and h-full).
	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('flex w-full min-h-0 flex-col gap-3 overflow-hidden', height, className)}>
			{variant === 'gauge' ? (
				<div className='flex w-full min-h-0 flex-1 flex-col items-center justify-center gap-3'>
					<Skeleton className='aspect-square h-3/4 rounded-full' />
					{isShort ? null : <Skeleton className='h-6 w-24' />}
				</div>
			) : variant === 'donut' ? (
				<div className='flex w-full min-h-0 flex-1 items-center justify-center gap-6'>
					<Skeleton className='aspect-square h-3/4 rounded-full' />
					{withLegend && !isShort ? (
						<div className='space-y-2'>
							{legendKeys.map(key => (
								<div key={key} className='flex items-center gap-2'>
									<Skeleton className='size-3 rounded-sm' />
									<Skeleton className='h-3 w-20' />
								</div>
							))}
						</div>
					) : null}
				</div>
			) : variant === 'rect' ? (
				<Skeleton className='w-full min-h-0 flex-1 rounded-lg' />
			) : (
				<div className='flex w-full min-h-0 flex-1 items-end gap-2 px-2'>
					{barKeys.map((key, i) => (
						<Skeleton key={key} className={cn('flex-1', BAR_HEIGHTS[i % BAR_HEIGHTS.length])} />
					))}
				</div>
			)}
			{withLegend && variant !== 'donut' && !isShort ? legend : null}
		</SkeletonRegion>
	)
}
